import {CommonModule} from '@angular/common';
import {Component, inject, signal} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {Store} from '@ngrx/store';
import {TextInputComponent} from '../../components/form-fields/text-input/text-input.component';
import {MatCardModule} from '@angular/material/card';
import {isLoginLoading} from '../../store/auth.selectors';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  PasswordInputComponent
} from '../../components/form-fields/password-input/password-input.component';
import {CommonService} from '../../services/common.service';
import * as AuthActions from '../../store/auth.actions';
import {
  PrimaryButtonComponent
} from '../../components/buttons/primary-button/primary-button.component';
import {emailRegex} from '../../constants/validation.constants'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthService} from '../../store/auth.service';
import {verifyEmail} from '../../store/auth.actions';
import {OtpInputComponent} from '../../components/form-fields/otp-input/otp-input.component';

@Component({
  selector: 'app-login-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TextInputComponent,
    PasswordInputComponent,
    PrimaryButtonComponent,
    OtpInputComponent
  ],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.css'
})
export class LoginFormComponent {
  private store = inject(Store);
  form;
  isLoading$ = this.store.select(isLoginLoading);
  authService = inject(AuthService);

  errorMessages = {
    email: '',
    password: '',
    otp: ''
  }

  errorMessageMap = {
    email: {
      required: 'Email is required.',
      pattern: 'Please enter a valid email address.'
    },
    password: {
      required: 'Password is required.'
    },
    otp: {
      required: 'OTP is required.',
      minlength: 'OTP must be 6 digits.',
      maxlength: 'OTP must be 6 digits.',
      pattern: 'OTP must contain numbers only.'
    }
  }

  showOtp = signal(false);

  otpForm;

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(emailRegex)]],
      password: ['', Validators.required]
    });

    this.otpForm = this.fb.group({
      otp: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern('^[0-9]*$')
      ]]
    });

    this.authService.verifyEmailOnLogin
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.form.disable();
        this.showOtp.set(true);
      })
  }

  checkForErrors(currentField?: any) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  checkForOtpErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.otpForm, this.errorMessageMap, currentField))
    };
  }

  submit() {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const {email, password} = this.form.value as any;
    this.store.dispatch(AuthActions.login({email, password}));
  }

  submitOtp() {
    if (!this.otpForm.valid) {
      this.checkForOtpErrors();
      return;
    }

    const otp = this.otpForm.get('otp')?.value;
    this.store.dispatch(verifyEmail({ otp }));
  }
}
